import type { InfiniteData, QueryClient } from '@tanstack/react-query';
import type {
  ChatLastMessage,
  ChatMessage,
  ChatMessagePage,
  Conversation,
} from '@/api/chat.api';

/**
 * The chat's React Query cache, and the only code that writes to it.
 *
 * Chat is the one place in the app where the socket carries the row itself
 * rather than "something changed, refetch": a message that waited for a
 * round trip would not feel live. So an incoming frame is folded into the
 * cache here, by pure functions, and every screen (the thread, the list, the
 * sidebar badge) reads the same cache and agrees.
 */
export const chatKeys = {
  all: ['chat'] as const,
  conversations: ['chat', 'conversations'] as const,
  contacts: ['chat', 'contacts'] as const,
  messages: (conversationId: string) =>
    ['chat', 'messages', conversationId] as const,
  search: (q: string, conversationId: string | null) =>
    ['chat', 'search', q, conversationId] as const,
};

/** `pages[0]` is the newest page; each later page is older. */
export type MessagesData = InfiniteData<ChatMessagePage, string | undefined>;

export function toLastMessage(message: ChatMessage): ChatLastMessage {
  return {
    id: message.id,
    senderId: message.senderId,
    content: message.content,
    createdAt: message.createdAt,
    deleted: message.deleted,
    refType: message.ref?.type ?? null,
    fileCount: message.files.length,
    priority: message.priority,
  };
}

/** The message names this reader, by name or "@everyone". */
export function mentionsMe(message: ChatMessage, staffId: string | null) {
  return (
    !!staffId &&
    message.senderId !== staffId &&
    (message.mentionsAll || message.mentionIds.includes(staffId))
  );
}

/** Every loaded message of a thread, oldest first. */
export function flattenMessages(data: MessagesData | undefined): ChatMessage[] {
  if (!data) return [];
  const out: ChatMessage[] = [];
  for (let i = data.pages.length - 1; i >= 0; i--) {
    out.push(...data.pages[i].items);
  }
  return out;
}

/**
 * Adds a message to its thread, or replaces the copy already there.
 *
 * "Already there" is matched by id, or by the `clientId` the sending tab
 * made up: the same message reaches that tab twice, once as the HTTP
 * response and once over the socket, in either order, and must settle the
 * one optimistic row rather than add a second.
 */
export function upsertMessage(queryClient: QueryClient, message: ChatMessage) {
  queryClient.setQueryData<MessagesData>(
    chatKeys.messages(message.conversationId),
    (data) => {
      if (!data || data.pages.length === 0) return data;
      let found = false;
      const pages = data.pages.map((page) => {
        const index = page.items.findIndex(
          (m) =>
            m.id === message.id ||
            (!!message.clientId && m.clientId === message.clientId),
        );
        if (index === -1) return page;
        found = true;
        const items = page.items.slice();
        items[index] = message;
        return { ...page, items };
      });
      if (!found) {
        pages[0] = { ...pages[0], items: [...pages[0].items, message] };
      }
      return { ...data, pages };
    },
  );
}

/**
 * An edited message: the new text in its thread, in every quote of it and,
 * when it is the newest, in the list's preview.
 */
export function applyMessageEdit(
  queryClient: QueryClient,
  message: ChatMessage,
) {
  queryClient.setQueryData<MessagesData>(
    chatKeys.messages(message.conversationId),
    (data) =>
      data && {
        ...data,
        pages: data.pages.map((page) => ({
          ...page,
          items: page.items.map((m): ChatMessage => {
            if (m.id === message.id)
              return { ...message, clientId: m.clientId };
            if (m.replyTo?.id === message.id) {
              return {
                ...m,
                replyTo: { ...m.replyTo, content: message.content },
              };
            }
            return m;
          }),
        })),
      },
  );
  queryClient.setQueryData<Conversation[]>(chatKeys.conversations, (current) =>
    current?.map((c) =>
      c.id === message.conversationId && c.lastMessage?.id === message.id
        ? { ...c, lastMessage: toLastMessage(message) }
        : c,
    ),
  );
}

/** My mute on a conversation changed (here or in another tab). */
export function setConversationMute(
  queryClient: QueryClient,
  conversationId: string,
  muted: boolean,
  mutedUntil: string | null,
) {
  queryClient.setQueryData<Conversation[]>(chatKeys.conversations, (current) =>
    current?.map((c) =>
      c.id === conversationId ? { ...c, muted, mutedUntil } : c,
    ),
  );
}

/**
 * Whether a conversation is muted now. The server answers with the mute as
 * it stood when the list was fetched; one that has since run out is over.
 */
export function isMutedNow(conversation: Conversation, now = Date.now()) {
  if (!conversation.muted) return false;
  return (
    !conversation.mutedUntil ||
    new Date(conversation.mutedUntil).getTime() > now
  );
}

/**
 * What a conversation adds to the sidebar badge. A muted one adds only what
 * reaches through the mute: mentions and urgent messages. (A message that is
 * both is counted twice here; the server's `/chat/unread` is exact, this is
 * the live estimate between fetches.)
 */
export function badgeCount(conversation: Conversation, now = Date.now()) {
  if (!isMutedNow(conversation, now)) return conversation.unreadCount;
  return Math.min(
    conversation.unreadCount,
    conversation.mentionCount + conversation.urgentCount,
  );
}

/** Empties a deleted message, and every quote of it, in a loaded thread. */
export function markMessageDeleted(
  queryClient: QueryClient,
  conversationId: string,
  messageId: string,
) {
  queryClient.setQueryData<MessagesData>(
    chatKeys.messages(conversationId),
    (data) =>
      data && {
        ...data,
        pages: data.pages.map((page) => ({
          ...page,
          items: page.items.map((m): ChatMessage => {
            if (m.id === messageId) {
              return { ...m, content: '', deleted: true, ref: null, files: [] };
            }
            if (m.replyTo?.id === messageId) {
              return {
                ...m,
                replyTo: { ...m.replyTo, content: '', deleted: true },
              };
            }
            return m;
          }),
        })),
      },
  );
}

export function removeMessage(
  queryClient: QueryClient,
  conversationId: string,
  messageId: string,
) {
  queryClient.setQueryData<MessagesData>(
    chatKeys.messages(conversationId),
    (data) =>
      data && {
        ...data,
        pages: data.pages.map((page) => ({
          ...page,
          items: page.items.filter((m) => m.id !== messageId),
        })),
      },
  );
}

const byRecency = (a: Conversation, b: Conversation) =>
  (b.lastMessageAt ?? '').localeCompare(a.lastMessageAt ?? '');

/**
 * Moves a conversation to the top of the list with its new last message.
 * Returns false when the conversation is not in the list yet (a first
 * message from someone), in which case the caller refetches the list.
 */
export function touchConversation(
  queryClient: QueryClient,
  message: ChatMessage,
  options: { countAsUnread: boolean; mentionsMe?: boolean },
): boolean {
  const list = queryClient.getQueryData<Conversation[]>(chatKeys.conversations);
  if (!list?.some((c) => c.id === message.conversationId)) return false;
  queryClient.setQueryData<Conversation[]>(chatKeys.conversations, (current) =>
    current
      ?.map((c) => {
        if (c.id !== message.conversationId) return c;
        // The same frame can arrive twice (HTTP response and socket).
        const isRepeat = c.lastMessage?.id === message.id;
        const counts = options.countAsUnread && !isRepeat;
        return {
          ...c,
          lastMessage: toLastMessage(message),
          lastMessageAt: message.createdAt,
          unreadCount: counts ? c.unreadCount + 1 : c.unreadCount,
          mentionCount:
            counts && options.mentionsMe ? c.mentionCount + 1 : c.mentionCount,
          urgentCount:
            counts && message.priority === 'urgent'
              ? c.urgentCount + 1
              : c.urgentCount,
        };
      })
      .sort(byRecency),
  );
  return true;
}

export function upsertConversation(
  queryClient: QueryClient,
  conversation: Conversation,
) {
  queryClient.setQueryData<Conversation[]>(
    chatKeys.conversations,
    (current) => {
      const rest = (current ?? []).filter((c) => c.id !== conversation.id);
      return [conversation, ...rest].sort(byRecency);
    },
  );
}

export function removeConversation(queryClient: QueryClient, id: string) {
  queryClient.setQueryData<Conversation[]>(chatKeys.conversations, (current) =>
    current?.filter((c) => c.id !== id),
  );
  // The thread's messages are left to expire by themselves. Removing them
  // here makes the still-mounted thread refetch a conversation the user was
  // just taken out of, which answers 404.
}

/** Records that `staffId` has read the conversation up to `at`. */
export function setConversationRead(
  queryClient: QueryClient,
  conversationId: string,
  staffId: string,
  at: string,
  isMe: boolean,
) {
  queryClient.setQueryData<Conversation[]>(chatKeys.conversations, (current) =>
    current?.map((c) =>
      c.id === conversationId
        ? {
            ...c,
            reads: { ...c.reads, [staffId]: at },
            unreadCount: isMe ? 0 : c.unreadCount,
            mentionCount: isMe ? 0 : c.mentionCount,
            urgentCount: isMe ? 0 : c.urgentCount,
          }
        : c,
    ),
  );
}

/** A deleted message that was the list's preview shows as deleted there too. */
export function markLastMessageDeleted(
  queryClient: QueryClient,
  conversationId: string,
  messageId: string,
) {
  queryClient.setQueryData<Conversation[]>(chatKeys.conversations, (current) =>
    current?.map((c) =>
      c.id === conversationId && c.lastMessage?.id === messageId
        ? {
            ...c,
            lastMessage: {
              ...c.lastMessage,
              content: '',
              deleted: true,
              refType: null,
              fileCount: 0,
              priority: 'normal',
            },
          }
        : c,
    ),
  );
}
